import React, { useEffect, useRef } from 'react'
import { FiAlertCircle } from '@react-icons/all-files/fi/FiAlertCircle'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { FiClock } from '@react-icons/all-files/fi/FiClock'
import styles from './BookingDateTimeFields.module.scss'

export const BookingDateTimeGroup = ({ children }) => (
  <div className={styles.group}>{children}</div>
)

export const BookingDateTimeNote = ({ children }) => (
  <div className={styles.note}>
    <FiClock aria-hidden='true' />
    <span>{children}</span>
  </div>
)

const BookingDateTimeField = ({
  autoFocus = false,
  error = '',
  errorAction,
  help,
  id,
  inputRef,
  label,
  min,
  name,
  onBlur,
  onChange,
  onInvalid,
  value,
  warning = '',
}) => {
  const helpId = `${id}_help`
  const errorId = `${id}_error`
  const warningId = `${id}_warning`
  const describedBy = [help ? helpId : null, error ? errorId : null, warning && !error ? warningId : null].filter(Boolean).join(' ') || undefined
  const dateValue = value?.slice(0, 10) || ''
  const timeValue = value?.slice(11, 16) || ''
  const minDate = min?.slice(0, 10) || undefined
  const minTime = minDate && dateValue === minDate ? min?.slice(11, 16) || undefined : undefined
  const committedValue = useRef(value || '')

  useEffect(() => {
    committedValue.current = value || ''
  }, [value])

  const fieldEvent = nextValue => ({
    currentTarget: { name, value: nextValue },
    target: { name, value: nextValue },
  })
  const mobileChange = (part, nextValue) => {
    const nextDate = part === 'date' ? nextValue : dateValue
    const nextTime = part === 'time' ? nextValue : (timeValue || '09:00')
    const nextDateTime = nextDate && nextTime ? `${nextDate}T${nextTime}` : ''
    onChange?.(fieldEvent(nextDateTime))
  }
  const commitDesktopValue = event => {
    const nextValue = event.currentTarget.value
    if (nextValue === committedValue.current) return
    committedValue.current = nextValue
    onChange?.(fieldEvent(nextValue))
  }
  const desktopBlur = event => {
    commitDesktopValue(event)
    onBlur?.(fieldEvent(event.currentTarget.value))
  }
  const mobileBlur = () => onBlur?.(fieldEvent(value || ''))
  const mobileInvalid = event => {
    event.preventDefault()
    onInvalid?.({ ...fieldEvent(value || ''), preventDefault: () => {} })
  }

  return (
    <div className={`${styles.field} ${error ? styles.hasError : warning ? styles.hasWarning : ''}`}>
      <label className={styles.label} htmlFor={id}>{label}</label>
      <div className={styles.control}>
        <FiCalendar className={styles.icon} aria-hidden='true' />
        <input
          ref={inputRef}
          className={`${styles.input} ${styles.desktopInput}`}
          id={id}
          name={name}
          type='datetime-local'
          value={value}
          min={min}
          required
          autoFocus={autoFocus}
          aria-describedby={describedBy}
          aria-errormessage={error ? errorId : undefined}
          aria-invalid={Boolean(error)}
          onBlur={desktopBlur}
          onChange={commitDesktopValue}
          onInput={commitDesktopValue}
          onInvalid={onInvalid}
        />
        <div className={styles.mobileInputs}>
          <label>
            <span>Date</span>
            <input
              type='date'
              value={dateValue}
              min={minDate}
              required
              aria-label={`${label} date`}
              aria-describedby={describedBy}
              aria-invalid={Boolean(error)}
              onBlur={mobileBlur}
              onChange={event => mobileChange('date', event.target.value)}
              onInvalid={mobileInvalid}
            />
          </label>
          <label>
            <span>Time</span>
            <input
              type='time'
              value={timeValue}
              min={minTime}
              step='60'
              required
              aria-label={`${label} time`}
              aria-describedby={describedBy}
              aria-invalid={Boolean(error)}
              onBlur={mobileBlur}
              onChange={event => mobileChange('time', event.target.value)}
              onInvalid={mobileInvalid}
            />
          </label>
        </div>
        <FiChevronDown className={styles.chevron} aria-hidden='true' />
      </div>
      {help && <p className={styles.help} id={helpId}>{help}</p>}
      <div className={styles.error} id={errorId} role={error ? 'alert' : undefined} aria-live='polite'>
        {error && <>
          <FiAlertCircle aria-hidden='true' />
          <span>{error}{errorAction && <> {errorAction}</>}</span>
        </>}
      </div>
      {!error && warning && <div className={styles.warning} id={warningId} role='status' aria-live='polite'>
        <FiAlertCircle aria-hidden='true' />
        <span>{warning}</span>
      </div>}
    </div>
  )
}

export default BookingDateTimeField
